import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import "@/App.css";
import './i18n';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from 'sonner';

// ✅ GLOBAL AXIOS CONFIG (IMPORTANT)
axios.defaults.withCredentials = true;
axios.defaults.baseURL = process.env.REACT_APP_BACKEND_URL;

// Components
import Header from './components/Header';
import Home from './pages/Home';
import DauterAI from './pages/DauterAI';
import Classes from './pages/Classes';
import ClassContent from './pages/ClassContent';
import About from './pages/About';
import Gallery from './pages/Gallery';
import Reviews from './pages/Reviews';
import FAQ from './pages/FAQ';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Contact from './pages/Contact';
import AdminDashboard from './pages/AdminDashboard';
import ContentViewer from './pages/ContentViewer';
import Login from './pages/Login'; // Now serves as Admin Login
import Footer from './components/Footer';
import Search from './pages/Search';
import News from './pages/News';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// ================= ADMIN AUTH CONTEXT =================
// Since regular users are removed, this context now only manages the Admin session.
export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // 'user' now exclusively refers to 'admin'
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      // Backend now returns the admin if logged in, or 401 if guest
      const res = await axios.get(`/api/auth/me`);
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (userData) => setUser(userData);

  const logout = async () => {
    try {
      await axios.post(`/api/auth/logout`);
    } catch (err) {
      console.error(err);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// ================= ADMIN PROTECTED ROUTE =================
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin h-12 w-12 border-t-2 border-b-2 border-red-500 rounded-full"></div>
      </div>
    );
  }

  // If there is no user, or the user is not an admin, redirect to home
  if (!user || user.role !== 'admin') {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
};

// ================= CONTENT PROTECTION =================
const useContentProtection = () => {
  useEffect(() => {
    const handleContextMenu = (e) => {
      if (e.target.closest('.protected-content')) {
        e.preventDefault();
        toast.error('Content is protected');
      }
    };

    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && ['s', 'p'].includes(e.key.toLowerCase())) {
        e.preventDefault();
        toast.error('Action disabled');
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
};

// ================= ROUTER =================
const AppRouter = () => {
  useContentProtection();

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      <Header />
      <main className="flex-1">
        <Routes>
          {/* ✅ PUBLIC ROUTES */}
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/dauter-ai" element={<DauterAI />} />
          <Route path="/classes" element={<Classes />} />
          <Route path="/classes/:level" element={<ClassContent />} />
          <Route path="/classes/:level/:stream" element={<ClassContent />} />
          <Route path="/content/:contentId" element={<ContentViewer />} />
          <Route path="/search" element={<Search />} />
          <Route path="/news" element={<News />} />
          <Route path="/about" element={<About />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/reviews" element={<Reviews />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:blogId" element={<BlogPost />} />
          <Route path="/contact" element={<Contact />} />

          {/* ✅ ADMIN PROTECTED ROUTES */}
          <Route
            path="/admin/*"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <Footer />
      <Toaster position="top-right" theme="dark" richColors />
    </div>
  );
};

// ================= MAIN APP =================
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
